import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Icon, makeStyles, Paper, Typography } from '@popmenu/common-ui';
import { Heart } from '@popmenu/web-icons';
import type { DishListsQuery_dishLists_DishListPaginationHandler_records_DishList as DishListType } from '../../../libs/gql/queries/dish_lists/dishListsQuery.generated';
import { formatNumber } from '../../../utils/numbers';
import { classNames } from '../../../utils/withStyles';
import CustomImg from '../../../shared/CustomImg';

type DishListCardProps = DishListType & {
  className: string
}

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
  },
  infoByLine: {
    color: theme.palette.text.secondary,
    display: 'block',
    fontSize: '0.8rem',
    marginBottom: theme.spacing(2),
  },
  infoCategoryName: {
    borderBottom: `2px solid ${theme.palette.red.main}`,
    color: theme.palette.red.main,
    display: 'inline-block',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(0.5),
    textTransform: 'uppercase',
  },
  infoContainer: {
    padding: theme.spacing(2),
  },
  infoDescription: {
    marginBottom: theme.spacing(2),
  },
  infoHeading: {
    fontSize: '2rem',
    fontWeight: 'normal',
    lineHeight: '2.5rem',
    marginBottom: theme.spacing(1),
  },
  infoPopsCount: {
    color: theme.palette.text.secondary,
  },
  linkOverlay: {
    borderRadius: theme.spacing(1),
    height: '100%',
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    textIndent: '-999em',
    top: 0,
    width: '100%',
  },
  photo: {
    borderRadius: theme.spacing(0.5, 0.5, 0, 0),
    height: '100%',
    left: 0,
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  photoContainer: {
    backgroundColor: theme.palette.gray.main,
    borderRadius: theme.spacing(0.5, 0.5, 0, 0),
    height: 0,
    marginBottom: theme.spacing(1),
    overflow: 'hidden',
    paddingTop: '75%',
    position: 'relative',
  },
}));

export const DishListCard = ({ className, ...dishList }: DishListCardProps) => {
  const classes = useStyles();
  const intl = useIntl();

  const dishListUrl = `/lists/${dishList.friendlySlug}`;

  return (
    <Paper
      className={classNames(
        classes.container,
        className,
      )}
      component="section"
      elevation={3}
    >
      <Box className={classes.photoContainer}>
        {dishList.photoUrl && (
          <CustomImg
            alt={dishList.name}
            className={classes.photo}
            size="sm"
            src={dishList.photoUrl}
          />
        )}
      </Box>
      <Box className={classes.infoContainer}>
        {dishList.categoryName && (
          <Typography className={classes.infoCategoryName} component="span">
            {dishList.categoryName}
          </Typography>
        )}
        <Typography className={classes.infoHeading} component="h2">
          {dishList.name}
        </Typography>
        <Typography className={classes.infoByLine} component="span">
          <FormattedMessage
            defaultMessage="by {userDisplayName}"
            values={{
              userDisplayName: dishList.userDisplayName,
            }}
          />
        </Typography>
        {dishList.description && (
          <Typography className={classes.infoDescription}>
            {dishList.description}
          </Typography>
        )}
        <Typography className={classes.infoPopsCount}>
          <Icon aria-label={intl.formatMessage({ defaultMessage: 'Likes' })} icon={Heart} />
          {formatNumber(dishList.popsCount)}
        </Typography>
      </Box>
      <Link
        className={classes.linkOverlay}
        data-cy="dish-list-card-overlay-link"
        to={dishListUrl}
      >
        {dishList.name}
      </Link>
    </Paper>
  );
};
